import {Component, computed, effect, inject, input, OnDestroy, output, signal, viewChild, ChangeDetectionStrategy} from "@angular/core"
import {NgStyle} from "@angular/common"
import {MatTooltip} from "@angular/material/tooltip"
import {ArConfiguratorActionsComponent} from "@app/template-editor/components/ar-configurator-actions/ar-configurator-actions.component"
import {SceneManagerService} from "@app/template-editor/services/scene-manager.service"
import {
    Camera,
    cameraLookAt,
    constrainTransformTargetableNode,
    getRootTemplateNodeForObjectPath,
    isNodeWithMatrixArrayLockedTransformParameter,
    ObjectId,
    SceneNodes,
    TemplateGraph,
    TransformMode,
} from "@cm/template-nodes"
import {Matrix4, Vector3} from "@cm/math"
import {mathIsWithinEpsilon} from "@app/template-editor/helpers/three-object"
import {TemplateAllVariationsComponent} from "@template-editor/components/template-all-variations/template-all-variations.component"
import {TemplateInspectorComponent} from "@template-editor/components/template-inspector/template-inspector.component"
import {TemplateSceneViewerOptionsComponent} from "@template-editor/components/template-scene-viewer-options/template-scene-viewer-options.component"
import {TemplateSelectVariationComponent} from "@template-editor/components/template-select-variation/template-select-variation.component"
import {
    SceneAggregationMode,
    TemplateTreeViewType,
    TemplateTreeViewTypeSelectorComponent,
} from "@template-editor/components/template-tree-view-type-selector/template-tree-view-type-selector.component"
import {TemplateTreeComponent} from "@template-editor/components/template-tree/template-tree.component"
import {
    TemplateViewType,
    TemplateViewTypeSelectorComponent,
} from "@template-editor/components/template-view-type-selector/template-view-type-selector.component"
import {ThreeTemplateSceneProviderComponent} from "@template-editor/components/three-template-scene-provider/three-template-scene-provider.component"
import {SceneCamera, ThreeTemplateSceneViewerComponent} from "@template-editor/components/three-template-scene-viewer/three-template-scene-viewer.component"
import {TemplateImageViewerComponent} from "@app/template-editor/components/template-image-viewer/template-image-viewer.component"
import {ButtonComponent} from "@app/common/components/buttons/button/button.component"
import {TemplateAddComponent} from "@app/template-editor/components/template-add/template-add.component"
import {MatProgressBarModule} from "@angular/material/progress-bar"
import {MatMenuModule} from "@angular/material/menu"
import {LocalPreviewRenderingService} from "@common/services/rendering/local-preview-rendering.service"
import {TemplateJobManagerService} from "@app/template-editor/services/template-job-manager.service"
import {ThreeSceneManagerService} from "@app/template-editor/services/three-scene-manager.service"
import {MeshGeometryService} from "@app/template-editor/services/mesh-geometry.service"
import {BakeTextureService} from "@app/template-editor/services/bake-texture.service"
import {BlenderConnectorService} from "@app/template-editor/services/blender-connector.service"
import {TemplateDropMeshFilesComponent} from "@app/template-editor/components/template-drop-mesh-files/template-drop-mesh-files.component"
import {PermissionsService} from "@common/services/permissions/permissions.service"
import {AuthService} from "@app/common/services/auth/auth.service"
import {TemplateType} from "@generated"
import {Router} from "@angular/router"
import {TitleSectionComponent} from "@app/platform/components/details/title-section/title-section.component"
import {TemplateTreeSceneNodeAggregationComponent} from "@app/template-editor/components/template-tree-scene-node-aggregation/template-tree-scene-node-aggregation.component"
import {LoadingSpinnerIconComponent} from "@app/common/components/progress/loading-spinner-icon/loading-spinner-icon.component"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-template-edit",
    templateUrl: "./template-edit.component.html",
    styleUrl: "./template-edit.component.scss",
    providers: [SceneManagerService, TemplateJobManagerService, LocalPreviewRenderingService, BlenderConnectorService, MeshGeometryService, BakeTextureService],
    imports: [
        TemplateViewTypeSelectorComponent,
        TemplateTreeViewTypeSelectorComponent,
        TemplateTreeComponent,
        TemplateAllVariationsComponent,
        TemplateSelectVariationComponent,
        ThreeTemplateSceneViewerComponent,
        TemplateSceneViewerOptionsComponent,
        ThreeTemplateSceneProviderComponent,
        TemplateInspectorComponent,
        MatTooltip,
        TemplateImageViewerComponent,
        ArConfiguratorActionsComponent,
        ButtonComponent,
        TemplateAddComponent,
        MatProgressBarModule,
        MatMenuModule,
        TemplateDropMeshFilesComponent,
        NgStyle,
        TitleSectionComponent,
        TemplateTreeSceneNodeAggregationComponent,
        LoadingSpinnerIconComponent,
    ],
})
export class TemplateEditComponent implements OnDestroy {
    readonly $leftPanelWidth = signal(288) // initial width in px
    readonly $inspectorWidth = signal(288)
    private mouseMove?: (moveEvent: MouseEvent) => void
    private mouseUp?: () => void
    readonly $isAlive = signal(true)

    removeMouseListeners() {
        if (this.mouseMove) {
            window.removeEventListener("mousemove", this.mouseMove)
            this.mouseMove = undefined
        }
        if (this.mouseUp) {
            window.removeEventListener("mouseup", this.mouseUp)
            this.mouseUp = undefined
        }
    }

    private onDividerMouseDown(event: MouseEvent, startWidth: number, setter: (w: number) => void) {
        event.preventDefault()
        const startX = event.clientX
        this.mouseMove = (moveEvent: MouseEvent) => {
            setter(Math.max(150, startWidth + moveEvent.clientX - startX))
        }
        this.mouseUp = () => this.removeMouseListeners()
        window.addEventListener("mousemove", this.mouseMove)
        window.addEventListener("mouseup", this.mouseUp)
    }

    onLeftPanelDividerMouseDown(event: MouseEvent) {
        this.onDividerMouseDown(event, this.$leftPanelWidth(), (w: number) => this.$leftPanelWidth.set(w))
    }

    onInspectorDividerMouseDown(event: MouseEvent) {
        this.onDividerMouseDown(event, this.$inspectorWidth(), (w: number) => this.$inspectorWidth.set(w))
    }

    ngOnDestroy() {
        this.$isAlive.set(false)
        this.removeMouseListeners()
    }

    readonly $preTitle = input<string | undefined>(undefined, {alias: "preTitle"})
    readonly $title = input<string | undefined>(undefined, {alias: "title"})
    readonly $postTitle = input<string | undefined>(undefined, {alias: "postTitle"})
    readonly $allowTitleEdit = input<boolean>(false, {alias: "allowTitleEdit"})
    readonly requestTitleChange = output<string>()

    readonly $templateGraph = input.required<TemplateGraph>({alias: "templateGraph"})
    readonly $defaultCustomerId = input.required<number>({alias: "defaultCustomerId"})
    readonly $templateId = input.required<string>({alias: "templateId"})
    readonly $templateType = input.required<TemplateType>({alias: "templateType"})
    readonly $templateRevisionId = input.required<string>({alias: "templateRevisionId"})

    readonly requestSave = output<(() => void) | undefined>()
    readonly requestSaveNewRevision = output<(() => void) | undefined>()
    readonly requestClose = output<void>()

    readonly $threeViewer = viewChild<ThreeTemplateSceneViewerComponent>("threeViewer")

    readonly $templateViewType = signal<TemplateViewType>("edit")
    readonly $templateTreeViewType = signal<TemplateTreeViewType>("tree")
    readonly $sceneAggregationMode = signal<SceneAggregationMode>("memoryUsage")

    readonly $selectedCameraId = signal<ObjectId | undefined>(undefined)
    readonly $cameraSnapshot = signal<{lockedTransform: number[]; target: number[]} | undefined>(undefined)

    onSelectedCameraIdChanged(cameraId: ObjectId | undefined) {
        this.$selectedCameraId.set(cameraId)
        const templateNode = this.getCameraTemplateNode(this.$selectedCameraNode())
        this.$cameraSnapshot.set(templateNode ? {lockedTransform: templateNode.parameters.lockedTransform, target: templateNode.parameters.target} : undefined)
    }

    readonly $cameraWasMoved = computed(() => {
        const snapshot = this.$cameraSnapshot()
        if (!snapshot) return false
        const templateNode = this.getCameraTemplateNode(this.$selectedCameraNode())
        if (!templateNode) return false
        return !(
            mathIsWithinEpsilon(new Matrix4(templateNode.parameters.lockedTransform), new Matrix4(snapshot.lockedTransform)) &&
            mathIsWithinEpsilon(Vector3.fromArray(templateNode.parameters.target), Vector3.fromArray(snapshot.target))
        )
    })

    readonly localSceneManagerService = inject(SceneManagerService)
    readonly localPreviewRenderingService = inject(LocalPreviewRenderingService)
    readonly $localThreeSceneManagerService = signal<ThreeSceneManagerService | undefined>(undefined)
    readonly blenderConnectorService = inject(BlenderConnectorService)
    readonly localMeshGeometryService = inject(MeshGeometryService)
    readonly bakeTextureService = inject(BakeTextureService)

    readonly permission = inject(PermissionsService)
    readonly authService = inject(AuthService)
    readonly $can = this.permission.$to
    readonly router = inject(Router)

    readonly $selectedCameraNode = computed(() => {
        const selectedCameraId = this.$selectedCameraId()

        if (selectedCameraId) {
            const cameraNode = this.localSceneManagerService.$cameras().find((x) => x.id === selectedCameraId)
            if (cameraNode) return cameraNode
        }
        return undefined
    })

    readonly $camera = computed<SceneCamera | undefined>(() => {
        const selectedCameraNode = this.$selectedCameraNode()
        return selectedCameraNode ? {parameters: selectedCameraNode, transform: selectedCameraNode.transform} : undefined
    })

    private getCameraTemplateNode(sceneNode: SceneNodes.Camera | undefined) {
        if (!sceneNode) return undefined
        const templateNode = getRootTemplateNodeForObjectPath(sceneNode.path)
        if (!(templateNode instanceof Camera) || !isNodeWithMatrixArrayLockedTransformParameter(templateNode)) return undefined
        return templateNode
    }

    /*Camera move mode: Movement in the editor is propagated to the params of the active camera.*/
    readonly $cameraMoveModeActive = computed(() => {
        const selectedCameraNode = this.$selectedCameraNode()
        if (!selectedCameraNode?.transform) return false
        const templateNode = this.getCameraTemplateNode(selectedCameraNode)
        return templateNode !== undefined && templateNode.parameters.automaticTarget === undefined && !templateNode.parameters.automaticVerticalTilt
    })

    onCameraViewChanged({transform, target}: {transform: Matrix4; target: Vector3}) {
        if (!this.$cameraMoveModeActive()) return
        const templateNode = this.getCameraTemplateNode(this.$selectedCameraNode())
        if (!templateNode) return

        let newTransform: Matrix4
        let newTarget: Vector3 | undefined
        if (templateNode.parameters.targeted) {
            newTransform = cameraLookAt(transform.getTranslation(), target)
            newTarget = target
        } else {
            const constrained = constrainTransformTargetableNode(
                this.localSceneManagerService,
                transform,
                {templateNode, part: "root"},
                TransformMode.Translate,
            )
            newTransform = constrained.transform
            newTarget = constrained.target
        }

        const currentTransform = new Matrix4(templateNode.parameters.lockedTransform)
        const currentTarget = Vector3.fromArray(templateNode.parameters.target)
        if (mathIsWithinEpsilon(newTransform, currentTransform) && (newTarget === undefined || mathIsWithinEpsilon(newTarget, currentTarget))) return

        this.localSceneManagerService.modifyTemplateGraph(() => {
            templateNode.updateParameters({lockedTransform: newTransform.toArray()})
            if (newTarget) templateNode.updateParameters({target: newTarget.toArray()})
        })
    }

    readonly $previewImageData = computed(() => {
        if (this.localSceneManagerService.$currentlyModifyingScene()) return null
        return this.localPreviewRenderingService.$previewImageData()
    })

    readonly $overlayPreviewImageData = computed(() => {
        return this.localPreviewRenderingService.$overlayHelpers()
    })

    readonly $numPendingTasks = computed(() => {
        return this.localSceneManagerService.$numPendingTasks() + this.localPreviewRenderingService.$numPendingTasks()
    })

    readonly $numTilesLoading = computed(() => {
        return this.$localThreeSceneManagerService()?.materialManagerService.textureTilesManager.$numTilesLoading() ?? 0
    })

    readonly templateGraphModified = output<boolean>()

    constructor() {
        this.localSceneManagerService.$showDimensionGuides.set(true)

        effect(() => {
            this.blenderConnectorService.$sendingAllowed.set(this.authService.$isStaff() || this.$templateType() === TemplateType.Product)
        })

        effect(() => {
            this.templateGraphModified.emit(this.localSceneManagerService.$templateGraphModified())
        })

        effect(() => {
            const templateGraph = this.$templateGraph()
            this.localSceneManagerService.$templateGraph.set(templateGraph)
            this.localSceneManagerService.sync(false).then(() => this.initializeEditorCamera())
        })

        effect(() => {
            const defaultCustomerId = this.$defaultCustomerId()
            this.localSceneManagerService.$defaultCustomerId.set(defaultCustomerId)
        })

        effect(() => {
            const templateRevisionId = this.$templateRevisionId()
            this.localSceneManagerService.$templateRevisionId.set(templateRevisionId)
        })

        effect(() => {
            const templateId = this.$templateId()
            this.localSceneManagerService.$templateId.set(templateId)
        })

        effect(() => {
            const nodes = this.localSceneManagerService.$scene()
            if (this.localSceneManagerService.$currentlyModifyingScene()) return
            this.localPreviewRenderingService.$sceneNodes.set(nodes)
        })

        effect(() => {
            const viewer = this.$threeViewer()
            if (!viewer) return
            const {width, height, pixelRatio} = viewer.$renderDimensions()
            const resolutionScale = pixelRatio * 1
            this.localPreviewRenderingService.$size.set([width * resolutionScale, height * resolutionScale])
        })

        effect(() => {
            const viewer = this.$threeViewer()
            if (!viewer) return
            const viewCamera = viewer.$viewCamera()
            this.localPreviewRenderingService.$cameraOverride.set(viewCamera)
        })

        effect(() => {
            this.localSceneManagerService.$computeDataObjectReferences.set(this.$templateTreeViewType() === "performance")
        })
    }

    private initializeEditorCamera() {
        const viewer = this.$threeViewer()
        const initialCamera = this.localSceneManagerService.$scene().find(SceneNodes.Camera.is)
        if (!viewer || !initialCamera) return
        viewer.initializeCamera(initialCamera)
    }

    save() {
        this.requestSave.emit(() => {
            this.localSceneManagerService.synchronizeSerializedTemplateGraph()
        })
    }

    saveNewRevision() {
        this.requestSaveNewRevision.emit(undefined)
    }

    navigateBack() {
        this.requestClose.emit()
    }

    onInititalizedThreeSceneManagerService(threeSceneManagerService: ThreeSceneManagerService | undefined) {
        this.$localThreeSceneManagerService.set(threeSceneManagerService)
        this.blenderConnectorService.setThreeSceneManagerService(threeSceneManagerService)
        this.localMeshGeometryService.threeSceneManagerService.set(threeSceneManagerService)
        this.bakeTextureService.threeSceneManagerService.set(threeSceneManagerService)
    }

    readonly $canChangeViewType = computed(() => {
        // was the viewer opened from the pictures page or the products/scenes page?
        const pageContext = this.router.url?.split("/")[1]
        // enterprise cloud orgs can switch the view type only when the editor was opened from the pictures page
        return this.$can().update.templateViewType(pageContext)
    })
}
